import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { DsTooltipDirectiveModule } from '@bmw-ds/components';

import { DealerProvider } from '../../../../auth/dealer/dealer.provider';
import { GcdmLanguageProvider } from '../../../consent/services/gcdm-language.provider';
import { NationalIdDocumentType } from '../../../customer/model/customer-information.model';
import { IdentityField } from '../../../customer/model/identity-field.model';
import { CustomerService } from '../../../customer/services/customer.service';
import { ProfileEditIdentityComponent } from './profile-edit-identity.component';

@Component({
  selector: 'cdf-profile-edit-identity-container',
  imports: [DsTooltipDirectiveModule, DsTooltipDirectiveModule, ProfileEditIdentityComponent],
  template: `
    <cdf-profile-edit-identity
      [(salutation)]="salutation"
      [(firstName)]="firstName"
      [(lastName)]="lastName"
      [(title)]="title"
      [(middleName)]="middleName"
      [(suffix)]="suffix"
      [(correspondenceLanguage)]="correspondenceLanguage"
      [(taxNumber)]="taxNumber"
      [(taxNumberCountry)]="taxNumberCountry"
      [(nationalId)]="nationalId"
      [(nationalIdDocumentType)]="nationalIdDocumentType"
      [disabled]="disabled()"
      [editingIdentityDisabled]="editingIdentityDisabled()"
      [invalidIdentityFields]="invalidIdentityFields()"
      [displayMxSalutationOption]="displayMxSalutationOption()"
      [displayProfDrTitleOption]="displayProfDrTitleOption()"
      [displayGbIeSalutationOptions]="displayGbIeSalutationOptions()"
      [availableNationalIdDocumentTypes]="availableNationalIdDocumentTypes()"
      [availableTaxNumberCountries]="customerService.countries()"
      [languages]="gcdmLanguageProvider.availableCorrespondenceLanguagesInThisCountry()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileEditIdentityContainer {
  protected readonly gcdmLanguageProvider = inject(GcdmLanguageProvider);
  private readonly dealerProvider = inject(DealerProvider);
  readonly customerService = inject(CustomerService);

  readonly salutation = model<string>();
  readonly firstName = model<string>();
  readonly lastName = model<string>();
  readonly title = model<string>();
  readonly middleName = model<string>();
  readonly suffix = model<string>();
  readonly correspondenceLanguage = model<string>();
  readonly taxNumber = model<string>();
  readonly taxNumberCountry = model<string>();
  readonly nationalId = model<string>();
  readonly nationalIdDocumentType = model<NationalIdDocumentType>();

  readonly disabled = input(false, { transform: booleanAttribute });
  readonly editingIdentityDisabled = input(false, { transform: booleanAttribute });
  readonly invalidIdentityFields = input<IdentityField[]>([]);

  protected readonly displayMxSalutationOption = computed(() =>
    ['DE', 'GB', 'IE'].includes(this.dealerProvider.activeCountry() ?? ''),
  );
  protected readonly displayProfDrTitleOption = computed(() => this.dealerProvider.activeCountry() === 'DE');
  protected readonly displayGbIeSalutationOptions = computed(() =>
    ['GB', 'IE'].includes(this.dealerProvider.activeCountry() ?? ''),
  );
  protected readonly availableNationalIdDocumentTypes = computed(() => {
    const country = this.dealerProvider.activeCountry();
    if (!country) {
      return Object.values(NationalIdDocumentType);
    } else if (['ES', 'PT'].includes(country)) {
      return [NationalIdDocumentType.PERSON_ID, NationalIdDocumentType.PASSPORT_ID];
    } else {
      return [NationalIdDocumentType.PERSON_ID];
    }
  });
}
